import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RotateCcw, Search, Download, Send, Printer } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import ServerPagination from "@/components/ServerPagination";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function RTOReportPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem("rto.pageSize") || "50", 10));
  const [kpi, setKpi] = useState({ rto_count: 0, total_shipments: 0, rto_pct: 0 });
  const [branches, setBranches] = useState([]);

  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [branchId, setBranchId] = useState("");
  const [courier, setCourier] = useState("");
  const [status, setStatus] = useState("");
  const [includeArchive, setIncludeArchive] = useState(true);

  const canFilterBranch = user && ["company_admin", "super_admin"].includes(user.role);

  useEffect(() => { if (canFilterBranch) api.get("/branches").then(r => setBranches(r.data)).catch(() => {}); }, [canFilterBranch]);

  const load = async (nextPage = page, nextSize = pageSize) => {
    const params = { page: nextPage, page_size: nextSize, include_archive: includeArchive };
    if (q) params.q = q;
    if (start) params.start = start;
    if (end) params.end = end;
    if (branchId) params.branch_id = branchId;
    if (courier) params.courier = courier;
    if (status) params.status = status;
    const { data } = await api.get("/reports/rto", { params });
    setItems(data.items || []); setTotal(data.total || 0); setPages(data.pages || 1);
    setKpi(data.kpi || { rto_count: 0, total_shipments: 0, rto_pct: 0 });
  };

  useEffect(() => { load(1, pageSize); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(page, pageSize); /* eslint-disable-next-line */ }, [page, pageSize]);

  const [applying, setApplying] = useState(false);
  const applyFilters = async () => {
    setApplying(true);
    try { setPage(1); await load(1, pageSize); }
    finally { setTimeout(() => setApplying(false), 250); }
  };
  const resetFilters = () => {
    setQ(""); setStart(""); setEnd(""); setBranchId(""); setCourier(""); setStatus(""); setIncludeArchive(true);
    setPage(1); setTimeout(() => load(1, pageSize), 0);
  };

  const doExport = async (fmt) => {
    // Reuse the log-export style by sending same filters to a dedicated URL — for now generate a client CSV as MVP
    try {
      const rows = [["RTO Date", "AWB/Tracking No", "Reference No", "Customer/Company", "Sender", "Recipient", "Courier", "Branch", "Location", "RTO Reason", "Current Status", "Delivery Attempts", "Created Date"]];
      items.forEach(p => rows.push([
        (p.updated_at || p.created_at || "").slice(0, 19).replace("T", " "),
        p.awb_number || p.tracking_number || "",
        p.reference_number || "",
        p.sender_company || p.recipient_company || "",
        p.sender_name || "", p.recipient_name || "",
        p.courier || "", p.branch_id || "", p.location || "",
        p.rto_reason || (p.notes || "").slice(0, 60), p.status || "",
        p.delivery_attempts || 1, (p.created_at || "").slice(0, 19).replace("T", " "),
      ]));
      const csv = rows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `rto-report.${fmt === "xlsx" ? "csv" : fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${items.length} row(s) as ${fmt.toUpperCase()}`);
    } catch (e) { toast.error("Export failed"); }
  };

  const doPrint = () => window.print();

  const doEmail = () => {
    const to = window.prompt("Email report to (comma-separated):", user?.email || "");
    if (!to) return;
    toast.info("Email report queued (backend hookup pending — use Download in the meantime).");
  };

  return (
    <PageShell
      title="RTO Report."
      overline={<><RotateCcw size={12} /> Reporting · Return-to-Office</>}
      subtitle="All Return-to-Office parcels with server-side pagination, filters, and export."
      testid="rto"
      actions={
        <div className="flex items-center gap-1.5">
          <button data-testid="rto-export-csv" onClick={() => doExport("csv")} className="inline-flex items-center gap-1.5 border border-border rounded-md px-3 py-1.5 text-xs font-semibold hover:border-primary hover:text-primary transition-colors bg-card"><Download size={12} /> CSV</button>
          <button data-testid="rto-export-xlsx" onClick={() => doExport("xlsx")} className="inline-flex items-center gap-1.5 border border-border rounded-md px-3 py-1.5 text-xs font-semibold hover:border-primary hover:text-primary transition-colors bg-card"><Download size={12} /> Excel</button>
          <button data-testid="rto-export-pdf" onClick={doPrint} className="inline-flex items-center gap-1.5 border border-border rounded-md px-3 py-1.5 text-xs font-semibold hover:border-primary hover:text-primary transition-colors bg-card"><Printer size={12} /> Print/PDF</button>
          <button data-testid="rto-email" onClick={doEmail} className="inline-flex items-center gap-1.5 border border-primary text-primary rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-primary hover:text-white transition-colors"><Send size={12} /> Email</button>
        </div>
      }
    >
      {/* KPI dashboard */}
      <section className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="rto-kpi">
        <div className="border border-border rounded-md bg-card p-4">
          <div className="overline">RTO Count</div>
          <div className="font-display text-3xl mt-2 text-primary">{kpi.rto_count.toLocaleString()}</div>
        </div>
        <div className="border border-border rounded-md bg-card p-4">
          <div className="overline">Total Shipments (scope)</div>
          <div className="font-display text-3xl mt-2">{kpi.total_shipments.toLocaleString()}</div>
        </div>
        <div className="border border-border rounded-md bg-card p-4">
          <div className="overline">RTO %</div>
          <div className="font-display text-3xl mt-2 text-primary">{kpi.rto_pct}%</div>
        </div>
      </section>

      {/* Filters */}
      <section className="border border-border rounded-md bg-card p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="rto-filters">
        <label className="block"><span className="overline">Search</span>
          <Input data-testid="rto-q" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyFilters()} className="mt-1 rounded-md" placeholder="Tracking, AWB, sender, recipient" />
        </label>
        <label className="block"><span className="overline">Start Date</span>
          <Input data-testid="rto-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 rounded-md" />
        </label>
        <label className="block"><span className="overline">End Date</span>
          <Input data-testid="rto-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 rounded-md" />
        </label>
        <label className="block"><span className="overline">Courier</span>
          <Input data-testid="rto-courier" value={courier} onChange={(e) => setCourier(e.target.value)} className="mt-1 rounded-md" />
        </label>
        {canFilterBranch && (
          <label className="block"><span className="overline">Branch</span>
            <select data-testid="rto-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}
                    className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
              <option value="">All</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
        )}
        <label className="block"><span className="overline">Status</span>
          <select data-testid="rto-status" value={status} onChange={(e) => setStatus(e.target.value)}
                  className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
            <option value="">Any</option>
            {["received", "in_transit", "out_for_delivery", "delivered", "returned", "cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="rto-archive" checked={includeArchive} onChange={(e) => setIncludeArchive(e.target.checked)} className="accent-[#BE1E1E]" />
          Include Archive
        </label>
        <div className="flex items-end gap-2">
          <Button onClick={applyFilters} disabled={applying} data-testid="rto-apply" className="rounded-md bg-primary text-primary-foreground">
            {applying ? <><Search size={14} className="mr-1.5 animate-pulse" /> Applying…</> : <><Search size={14} className="mr-1.5" /> Apply</>}
          </Button>
          <Button variant="outline" onClick={resetFilters} data-testid="rto-reset" className="rounded-md">Reset</Button>
        </div>
      </section>

      {/* Table */}
      <div className="border border-border bg-card overflow-x-auto rounded-md">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border overline text-left bg-muted/30">
              <th className="p-2 px-3">RTO Date</th>
              <th className="p-2 px-3">AWB/Tracking</th>
              <th className="p-2 px-3">Ref No</th>
              <th className="p-2 px-3">Customer/Sender</th>
              <th className="p-2 px-3">Recipient</th>
              <th className="p-2 px-3">Courier</th>
              <th className="p-2 px-3">Branch</th>
              <th className="p-2 px-3">Location</th>
              <th className="p-2 px-3">RTO Reason</th>
              <th className="p-2 px-3">Status</th>
              <th className="p-2 px-3">Attempts</th>
              <th className="p-2 px-3">Created</th>
            </tr>
          </thead>
          <tbody data-testid="rto-table">
            {items.length === 0 && (
              <tr><td colSpan={12} className="p-8 text-center text-muted-foreground text-sm">No RTO records for the current filters.</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-border hover:bg-muted/40" data-testid={`rto-row-${p.id}`}>
                <td className="p-2 px-3 mono text-xs">{((p.updated_at || p.created_at || "").slice(0, 19).replace("T", " "))}</td>
                <td className="p-2 px-3 mono text-xs font-semibold">{p.awb_number || p.tracking_number || "—"}</td>
                <td className="p-2 px-3 mono text-xs">{p.reference_number || p.tracking_number || "—"}</td>
                <td className="p-2 px-3 text-xs">{p.sender_name || "—"}</td>
                <td className="p-2 px-3 text-xs">{p.recipient_name || "—"}</td>
                <td className="p-2 px-3 mono text-xs">{p.courier || "—"}</td>
                <td className="p-2 px-3 text-xs">{p.branch_id || "—"}</td>
                <td className="p-2 px-3 text-xs">{p.location || p.recipient_department || "—"}</td>
                <td className="p-2 px-3 text-xs">{p.rto_reason || (p.notes || "").slice(0, 60) || "—"}</td>
                <td className="p-2 px-3">
                  <span className="text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5 rounded mono">{p.status || "—"}</span>
                </td>
                <td className="p-2 px-3 mono text-xs">{p.delivery_attempts || 1}</td>
                <td className="p-2 px-3 mono text-xs">{(p.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ServerPagination
        page={page} pageSize={pageSize} total={total} pages={pages}
        onPageChange={(n) => setPage(n)}
        onPageSizeChange={(s) => { setPageSize(s); localStorage.setItem("rto.pageSize", String(s)); setPage(1); }}
        storageKey="rto.pageSize"
        testidPrefix="rto-pg"
      />
    </PageShell>
  );
}
